/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject, VNode } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { Scale } from '#utils/UNSAFE_visTypes/chart';
import {
  getCursorType,
  getMarqueeDims,
  isPointOutsideTargetSpace,
  isPointInsideSpace,
  getPanButtonVisibility,
  getZoomButtonVisibility,
  getSelectButtonVisibility,
  getResolvedDragMode
} from './utils';
import { isTouch } from '#utils/UNSAFE_visUtils/clientUtils';
import { useVisDragModeControls } from '#hooks/PRIVATE_useVisDragModeControls';
import { Marquee } from './Marquee';
import { SelectionDetail } from '#hooks/PRIVATE_useVisSelection/useSelection';
import { AxisInfo } from '#PRIVATE_Axis/axis.types';
import { getElementData } from '#utils/UNSAFE_visUtils';

type MarqueeProps<K, D> = {
  rootRef: RefObject<HTMLElement>;
  chartWidth: number;
  plotAreaSpace: Dimension;
  xSpace: Dimension;
  ySpace: Dimension;
  isZoomScroll: boolean;
  isMultiSelection: boolean;
  onSelectionChange?: (detail: SelectionDetail<K, D>) => void;
  xScale: Scale;
  onViewportChange?: (x: { xMin?: number; yMin?: number; xMax?: number; yMax?: number }) => void;
  dragMode: 'user' | 'select' | 'zoom' | 'pan' | 'off';
  getDataById: (id: K) => D | undefined;
  selectedIds?: Set<K>;
  isRtl: boolean;
  xAxisInfo: AxisInfo;
};

export function useChartMarquee<K, D>({
  rootRef,
  plotAreaSpace,
  xSpace,
  ySpace,
  dragMode,
  chartWidth,
  isMultiSelection,
  onViewportChange,
  isZoomScroll,
  xScale,
  selectedIds,
  getDataById,
  onSelectionChange,
  isRtl,
  xAxisInfo
}: MarqueeProps<K, D>) {
  const isDeviceTouch = isTouch();
  const isHovered = useRef(false);
  const numOfActiveTouches = useRef(0);

  const { min, max, viewportMax, viewportMin, minAxisExtent } = xAxisInfo;

  const initialPointRef = useRef<{
    x: number;
    y: number;
    isMeta?: boolean;
  }>();

  const showPanButton = getPanButtonVisibility(isZoomScroll, dragMode, isDeviceTouch);
  const showZoomButton = getZoomButtonVisibility(isZoomScroll, isDeviceTouch, dragMode);
  const showSelectButton = getSelectButtonVisibility(
    isMultiSelection,
    isZoomScroll,
    isDeviceTouch,
    dragMode
  );

  const { dragButtonDivRef, toggleButtonContent, userDragMode } = useVisDragModeControls({
    componentWidth: chartWidth,
    plotAreaSpace,
    isRtl,
    showPanButton,
    showZoomButton,
    showSelectButton,
    dragMode
  });

  // for drag --> dragging cursor rerender in pan mode
  const [isPointerDown, setIsPointerDown] = useState(false);
  const isDragActiveRef = useRef(false);

  const [finalPoint, setFinalPoint] = useState<{ x: number; y: number }>();

  const selectedIdsRef = useRef<Set<K>>(new Set());

  const isUserDragModeButtonShown =
    (showPanButton && userDragMode === 'pan') ||
    (showSelectButton && userDragMode === 'select') ||
    (showZoomButton && userDragMode === 'zoom');

  const mode = getResolvedDragMode(
    isUserDragModeButtonShown,
    isZoomScroll,
    isDeviceTouch,
    dragMode,
    userDragMode,
    isMultiSelection
  );

  const dims = getMarqueeDims(
    initialPointRef.current,
    finalPoint,
    plotAreaSpace,
    xSpace,
    ySpace,
    mode === 'zoom' // We need to revisit this for scatter chart and others charts
  );

  const onPointerEnter = () => {
    isHovered.current = true;
  };

  const onPointerDown = (event: PointerEvent) => {
    const info = getElementData(rootRef, event.target as HTMLElement);
    if (info?.['ojPrivateObject'] === 'bar' || info?.['ojPrivateObject'] === 'item') {
      return;
    }
    // prevent setIsPointerDown(true) when clicked on button
    // otherwise the button will disapper before being clicked.
    if (dragButtonDivRef?.current?.contains(event.target as HTMLElement)) {
      return;
    }

    const x = event.offsetX;
    const y = event.offsetY;

    setIsPointerDown(true);

    if (
      isPointOutsideTargetSpace(x, y, plotAreaSpace, xSpace, ySpace) ||
      (mode === 'zoom' && isPointInsideSpace(x, y, ySpace))
    ) {
      return;
    }

    initialPointRef.current = { x, y };
  };

  const onPointerLeave = (event: PointerEvent) => {
    isHovered.current = false;
    if (dims && isDeviceTouch && mode != 'off') {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!isPointerDown) {
      return;
    }

    isDragActiveRef.current = true;

    if (event.metaKey && initialPointRef.current) {
      initialPointRef.current.isMeta = true;
    }

    const x = event.offsetX;
    const y = event.offsetY;

    if (
      isPointOutsideTargetSpace(x, y, plotAreaSpace, xSpace, ySpace) ||
      (mode === 'zoom' && isPointInsideSpace(x, y, ySpace))
    ) {
      return;
    }

    const elementData = getElementData(rootRef, event.target as HTMLElement);
    const elemType = elementData?.['ojPrivateObject'];
    const originatedFromOverview =
      elemType === 'window' || elemType === 'leftHandle' || elemType === 'rightHandle';

    if (
      !originatedFromOverview &&
      mode === 'pan' &&
      onViewportChange &&
      (!isDeviceTouch || (isDeviceTouch && numOfActiveTouches.current === 1))
    ) {
      const axisDelta =
        xScale.invert(x) - xScale.invert((finalPoint?.x || initialPointRef.current?.x)!);

      const viewportRange = viewportMax - viewportMin;

      let xMin = viewportMin - axisDelta;
      let xMax = viewportMax - axisDelta;

      if (xMin < min) {
        xMin = min;
        xMax = xMin + viewportRange;
      }

      if (xMax > max) {
        xMax = max;
        xMin = xMax - viewportRange;
      }

      if (xMin != viewportMin && xMax != viewportMax && Math.abs(xMax - xMin) >= minAxisExtent) {
        onViewportChange({
          xMin,
          xMax
        });
      }
    }

    setFinalPoint({ x, y });
  };

  const onPointerUp = (event: PointerEvent) => {
    setIsPointerDown(false);
    setFinalPoint(undefined);
    initialPointRef.current = undefined;
    isDragActiveRef.current = false;

    if (dragButtonDivRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    if (!dims) {
      return;
    }

    if (mode === 'zoom') {
      const { x1, x2 } = dims;
      let xMin = Math.min(xScale.invert(x1), xScale.invert(x2));
      let xMax = Math.max(xScale.invert(x1), xScale.invert(x2));
      if (Math.abs(xMax - xMin) < minAxisExtent) {
        const mid = (xMin + xMax) / 2;
        xMin = Math.max(min, mid - minAxisExtent / 2);
        xMax = Math.min(max, xMin + minAxisExtent);
      }
      onViewportChange?.({
        xMin,
        xMax
      });
    }

    if (mode === 'select') {
      const ids = [
        ...new Set([
          ...selectedIdsRef.current,
          ...(event.metaKey ? selectedIds || new Set<K>() : new Set<K>())
        ])
      ];
      const data = ids.map((id) => getDataById(id));
      onSelectionChange?.({ ids, data });
      selectedIdsRef.current = new Set<K>();
    }
  };

  // if mode is 'off', we still want to show the toggleButton.
  // pointerUp will not fire because browser will have already claimed the
  // pointer
  const onTouchEnd = (event: TouchEvent) => {
    setIsPointerDown(false);
    setFinalPoint(undefined);
    initialPointRef.current = undefined;
    numOfActiveTouches.current = event.touches.length;
  };

  const onTouchStart = (event: TouchEvent) => {
    numOfActiveTouches.current = event.touches.length;
  };

  let marqueeContent: VNode | undefined;

  const isPointInsideMarquee =
    mode === 'select' && dims
      ? (id: K, point: { x: number; y: number }) => {
          if (initialPointRef.current?.isMeta && selectedIds?.has(id)) {
            return true;
          }
          const { x1, x2, y1, y2 } = dims;
          const { x, y } = point;
          if (isPointInsideSpace(x, y, { x: x1, width: x2 - x1, y: y1, height: y2 - y1 })) {
            selectedIdsRef.current?.add(id);
            return true;
          } else {
            selectedIdsRef.current?.delete(id);
          }
          return false;
        }
      : undefined;

  if (dims && (mode === 'zoom' || mode === 'select')) {
    marqueeContent = <Marquee {...dims} />;
  }

  return {
    ignoreSelectPointerUp: (mode === 'pan' || mode === 'zoom') && isDragActiveRef.current,
    cursor: getCursorType(mode, isPointerDown),
    yAxisCursor: mode !== 'zoom' ? getCursorType(mode, isPointerDown) : 'initial',
    marqueeContent,
    toggleButtonContent:
      !isPointerDown &&
      ((!isDeviceTouch && isHovered.current) || isDeviceTouch) &&
      toggleButtonContent,
    isPointInsideMarquee,
    dragMode: mode,
    marqueeProps:
      dragMode != 'off'
        ? {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerLeave,
            onPointerEnter,
            onTouchEnd,
            onTouchStart
          }
        : {}
  };
}
